import dayjs from '@/helpers/dayjs';
import { yupAddress, yupBoolean, yupMixed, yupNumber, yupObject, yupPeriodicValue, yupString } from '../../yup';
import type { DepositTypeEnum, IncomingProperty, PropertyTypeEnum, StampDutyStatusEnum } from '@/types';
import {
  DepositType,
  PropertyPurpose,
  propertyTypesWithPurpose,
  StampDutyStatus,
  standardBridgeTypes,
} from '@/helpers/const';
import { useFeatureFlags, FeatureFlag } from '@/helpers/useFeatureFlags';

const isNegativeGearingFy27Enabled = () => useFeatureFlags().isEnabled(FeatureFlag.NEGATIVE_GEARING_FY27);

const isNewBuildQuestionApplicable = (propertyType: PropertyTypeEnum, propertyPurpose?: string) =>
  isNegativeGearingFy27Enabled() &&
  standardBridgeTypes.includes(propertyType as 'residential' | 'rural_residential' | 'off_plan_residential') &&
  propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY;

export const incomingPropertySchemaV2 = yupObject<IncomingProperty>({
  contractForSale: yupBoolean().required().label('Contract for sale response'),
  propertyType: yupMixed<PropertyTypeEnum>().required().label('Property type'),
  address: yupAddress().when('propertySelected', {
    is: (propertySelected: boolean) => propertySelected === undefined || propertySelected === true,
    then: (schema) => schema.required().label('Property address'),
  }),
  propertyPurpose: yupString().when('propertyType', {
    is: (type: PropertyTypeEnum) =>
      propertyTypesWithPurpose.includes(
        type as 'residential' | 'rural_residential' | 'off_plan_residential' | 'vacant_land',
      ),
    then: (schema) => schema.required().label('Property purpose'),
  }),
  receiveRentalIncome: yupBoolean().when('propertyType', {
    is: (type: PropertyTypeEnum) =>
      standardBridgeTypes.includes(type as 'residential' | 'rural_residential' | 'off_plan_residential'),
    then: (schema) => schema.required().label('Rental income response'),
  }),
  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('receiveRentalIncome', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Expected rental income'),
  // Negative Gearing (FY27) — only enforced when the feature flag is enabled
  isNewBuild: yupBoolean()
    .when(['propertyType', 'propertyPurpose'], ([propertyType, propertyPurpose], schema) =>
      isNewBuildQuestionApplicable(propertyType, propertyPurpose) ? schema.required() : schema,
    )
    .label('New build response'),
  isContractExchanged: yupBoolean()
    .when(
      ['propertyType', 'propertyPurpose', 'isNewBuild', 'contractForSale'],
      ([propertyType, propertyPurpose, isNewBuild, contractForSale], schema) =>
        isNewBuildQuestionApplicable(propertyType, propertyPurpose) && isNewBuild === false && contractForSale === true
          ? schema.required()
          : schema,
    )
    .label('Contract exchange response'),
  settlementDate: yupString()
    .required()
    .isoDate()
    .test('validDate', '${path} must be within 12 months', (value) => {
      const date = dayjs(value);
      return date.isAfter(dayjs(), 'day') && date.isBefore(dayjs().add(1, 'year'), 'day');
    })
    .label('Purchase date'),
  purchaseAmount: yupNumber().required().label('Purchase price'),
  depositAmount: yupNumber()
    .when('purchaseAmount', ([purchaseAmount], schema) =>
      schema.max(purchaseAmount, `The deposit cannot be more than the purchase price`),
    )
    .required()
    .label('Deposit amount'),
  depositType: yupMixed<DepositTypeEnum>().oneOf(Object.values(DepositType)).required().label('Deposit payment method'),
  stampDutyStatus: yupMixed<StampDutyStatusEnum>()
    .oneOf(Object.values(StampDutyStatus))
    // already_paid is only valid if contract for sale exists
    .when('propertyType', {
      is: (type: PropertyTypeEnum) => type !== 'retirement_land_lease_or_community_home',
      then: (schema) =>
        schema
          .when('contractForSale', {
            is: true,
            then: (schema) => schema.oneOf(Object.values(StampDutyStatus)).required().label('Stamp duty response'),
            otherwise: (schema) =>
              schema.oneOf(Object.values(StampDutyStatus).filter((item) => item !== StampDutyStatus.ALREADY_PAID)),
          })
          .required()
          .label('Stamp duty response'),
    }),
  transferFeeCovered: yupBoolean().required().label('Transfer fee'),
});
